import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Calendar, MessageSquare, PhoneCall, Video, Play, Star, CheckCircle2, Users, ArrowRight } from "lucide-react";
import NudgeToggle from "@user/components/NudgeToggle";

const TYPE_META = {
  chat: { icon: MessageSquare, label: "Chat", color: "text-[#1E3A8A]" },
  audio: { icon: PhoneCall, label: "Audio", color: "text-emerald-700" },
  video: { icon: Video, label: "Video", color: "text-purple-700" },
};

// Default session duration — 15 min for chat, 30 min for audio/video
const DURATION_MIN = { chat: 15, audio: 30, video: 30 };

function parseSlot(c) {
  if (!c?.slot_date || !c?.slot_time) return null;
  // slot_time may be "14:30" or "14:30-14:45". Take the start.
  const start = String(c.slot_time).split(/[–-]/)[0].trim();
  const iso = `${c.slot_date}T${start}:00`;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d;
}

// Returns one of: 'upcoming' | 'live' | 'completed'
function lifecycleState(c, now = new Date()) {
  if (c.status === "completed") return "completed";
  const slot = parseSlot(c);
  if (!slot) return "upcoming";
  const mins = DURATION_MIN[c.consultation_type] || 15;
  const end = new Date(slot.getTime() + mins * 60 * 1000);
  if (now < slot) return "upcoming";
  if (now >= slot && now < end) return "live";
  return "completed";
}

function formatRelative(target, now = new Date()) {
  const diffMs = target.getTime() - now.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 60 && mins >= 0) return `Starts in ${mins} min${mins === 1 ? "" : "s"}`;
  if (mins < 0) return "Started";
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Starts in ${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `In ${days} day${days === 1 ? "" : "s"}`;
}

function formatSlot(c) {
  const d = parseSlot(c);
  if (!d) return c.slot_date || "Time TBD";
  const opts = { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true };
  return d.toLocaleString("en-IN", opts);
}

export default function UpcomingConsultations({ consults }) {
  const [, tick] = useState(0);

  // Re-render every 30s so "Starts in X" + Live state update without a refresh
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);

  if (!consults || consults.length === 0) {
    return (
      <div data-testid="upcoming-empty" className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
        <div className="h-12 w-12 mx-auto rounded-2xl bg-[#FBF4DF] text-[#8a6d14] grid place-items-center">
          <Users size={22} />
        </div>
        <p className="mt-3 text-sm text-slate-500">
          No consultations yet. <Link to="/lawyers" className="text-[#1E3A8A] font-semibold">Find a lawyer →</Link>
        </p>
      </div>
    );
  }

  // Sort: live first, then upcoming (closest first), then completed (most recent first)
  const rank = (c) => {
    const st = lifecycleState(c);
    if (st === "live") return 0;
    if (st === "upcoming") return 1;
    return 2;
  };
  const sorted = [...consults].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    const sa = parseSlot(a)?.getTime() || 0;
    const sb = parseSlot(b)?.getTime() || 0;
    return ra === 2 ? sb - sa : sa - sb;
  });

  const upcoming = sorted.filter((c) => lifecycleState(c) !== "completed");
  const completed = sorted.filter((c) => lifecycleState(c) === "completed");

  return (
    <div className="space-y-6">
      {upcoming.length > 0 && (
        <section data-testid="upcoming-section">
          <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-500 mb-3">
            Upcoming Consultations
          </h3>
          <div className="space-y-3">
            {upcoming.map((c) => <ConsultCard key={c.id} consult={c} />)}
          </div>
        </section>
      )}
      {completed.length > 0 && (
        <section data-testid="completed-section">
          <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-500 mb-3">
            Completed
          </h3>
          <div className="space-y-3">
            {completed.map((c) => <ConsultCard key={c.id} consult={c} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function ConsultCard({ consult }) {
  const state = lifecycleState(consult);
  const meta = TYPE_META[consult.consultation_type] || TYPE_META.chat;
  const Icon = meta.icon;
  const slotDate = parseSlot(consult);
  const paid = consult.payment_status === "paid";

  return (
    <div
      data-testid={`consult-card-${consult.id}`}
      className={`bg-white border rounded-2xl p-4 md:p-5 transition-all ${
        state === "live" ? "border-emerald-500 shadow-lg ring-2 ring-emerald-100" : "border-slate-200 hover:shadow-md"
      }`}
    >
      <div className="flex items-start gap-3 md:gap-4">
        {consult.lawyer_photo ? (
          <img src={consult.lawyer_photo} alt={consult.lawyer_name} className="h-12 w-12 md:h-14 md:w-14 rounded-xl object-cover shrink-0" />
        ) : (
          <div className="h-12 w-12 md:h-14 md:w-14 rounded-xl adv-navy text-[#D4AF37] grid place-items-center font-bold shrink-0">
            {(consult.lawyer_name || "L")[0]}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <p className="font-extrabold text-[#0A2540] truncate" style={{ fontFamily: "Poppins" }}>
                {consult.lawyer_name}
              </p>
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                <Icon size={12} className={meta.color} />
                <span className="font-semibold">{meta.label}</span>
                <span className="text-slate-300">·</span>
                <Calendar size={11} />
                <span>{formatSlot(consult)}</span>
              </p>
            </div>
            <StatusBadge state={state} />
          </div>

          {/* Context row: time-remaining message OR live banner */}
          {state === "upcoming" && slotDate && (
            <p
              data-testid={`consult-countdown-${consult.id}`}
              className="mt-2 text-xs text-slate-600 inline-flex items-center gap-1 bg-slate-50 rounded-full px-2.5 py-1"
            >
              <Clock size={11} /> {formatRelative(slotDate)}
            </p>
          )}
          {state === "live" && (
            <p className="mt-2 text-xs text-emerald-700 font-bold inline-flex items-center gap-1.5 bg-emerald-50 rounded-full px-2.5 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live Now
            </p>
          )}
          {!paid && state !== "completed" && (
            <p className="mt-2 text-[11px] text-amber-700 font-bold">
              Payment pending · complete payment to start
            </p>
          )}

          {/* CTAs */}
          <div className="mt-3 flex flex-wrap gap-2">
            {state === "live" && paid && (
              <Button
                asChild
                data-testid={`start-consult-${consult.id}`}
                className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4"
              >
                <Link to={`/consultations/${consult.id}`}>
                  <Play size={13} fill="currentColor" /> Start Consultation
                </Link>
              </Button>
            )}
            {state === "upcoming" && paid && (
              <>
                <Button
                  asChild
                  variant="outline"
                  data-testid={`view-consult-${consult.id}`}
                  className="rounded-full border-[#0A2540]/20 hover:bg-[#0A2540] hover:text-white font-semibold text-xs h-9 px-4"
                >
                  <Link to={`/consultations/${consult.id}`}>
                    View details <ArrowRight size={13} />
                  </Link>
                </Button>
                <NudgeToggle consult={consult} slotDate={slotDate} />
              </>
            )}
            {state === "upcoming" && !paid && (
              <Button
                asChild
                data-testid={`pay-consult-${consult.id}`}
                className="rounded-full adv-btn-primary font-bold text-xs h-9 px-4"
              >
                <Link to={`/consultations/${consult.id}`}>
                  Complete payment <ArrowRight size={13} />
                </Link>
              </Button>
            )}
            {state === "completed" && (
              <>
                <Button
                  asChild
                  variant="outline"
                  data-testid={`view-consult-${consult.id}`}
                  className="rounded-full font-semibold text-xs h-9 px-4"
                >
                  <Link to={`/consultations/${consult.id}`}>
                    <CheckCircle2 size={13} /> View summary
                  </Link>
                </Button>
                <Button
                  asChild
                  data-testid={`review-consult-${consult.id}`}
                  className="rounded-full adv-btn-gold font-bold text-xs h-9 px-4"
                >
                  <Link to={`/consultations/${consult.id}?review=1`}>
                    <Star size={13} fill="currentColor" /> Leave Review
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ state }) {
  if (state === "live")
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-live">
        Live
      </Badge>
    );
  if (state === "upcoming")
    return (
      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-upcoming">
        Upcoming
      </Badge>
    );
  return (
    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 rounded-full font-bold uppercase tracking-wider text-[10px]" data-testid="badge-completed">
      Completed
    </Badge>
  );
}
